// Upload-error event — typed reasons emitted by the chat backend on the SSE
// stream when a file attachment is rejected (or when the Files API upload
// fell back to inline base64). The reason union mirrors the server's
// `AttachmentRejectionReason` plus the reasons the chat handler emits itself:
// `upload-failed-using-inline-fallback` and `image-dimensions-too-large` (image
// over the per-dimension pixel ceiling, PLT-6059) — see
// `src/services/main/upserve/modules/chat/anthropic-files.ts` and
// `chat/index.ts` (the upload-error emission loop).
//
// This lives in its own module (rather than in `upright-chat-shared.ts`) so the
// pure message formatter can be unit-tested without dragging the chat module
// graph — which touches `localStorage` at load time — into the headless
// (no-DOM) unit-test environment. See PLT-5450.
// ---------------------------------------------------------------------------
import { describeSupportedAttachmentFormats } from './attachment-validation';

/**
 * Narrow set of upload-error reasons the server emits. Keep in sync with the
 * server-side `AttachmentRejectionReason` plus the synthetic reasons emitted
 * from the chat handler: `upload-failed-using-inline-fallback` (Files API
 * upload failed, degraded to inline base64) and the `*-conversion-failed`
 * reasons raised when a server-side format conversion (DOCX→text, XLSX→CSV)
 * can't parse the uploaded file.
 */
export type UploadErrorReason =
  | 'too-large'
  | 'too-many'
  | 'invalid-mime'
  | 'malformed'
  | 'upload-failed-using-inline-fallback'
  // EU Bedrock inline path (PLT-5437): combined size of all attachments
  // exceeds the per-turn request budget, and the PDF page-count limit.
  | 'attachments-too-large-combined'
  | 'too-many-pages'
  // Image width or height over Anthropic's 8000px per-dimension ceiling
  // (PLT-6059) — dropped up front on both providers, since the 400 it would
  // otherwise cause is not retryable and kills the whole turn.
  | 'image-dimensions-too-large'
  // Server-side format conversion couldn't parse the file (PLT-5826 DOCX→text;
  // the XLSX→CSV path emits the analogous xlsx reason).
  | 'docx-conversion-failed'
  | 'xlsx-conversion-failed'
  // The active model provider doesn't accept attachments at all (PLT-5430
  // fail-closed drop). No provider sets this today, but the chat handler emits
  // it (chat/index.ts) so a future attachment-incapable provider can never
  // ship file bytes — and this build must render it, not fall through to the
  // generic message (PLT-5861).
  | 'provider-unsupported';

/**
 * Render an `upload-error` SSE event payload as a single user-facing string.
 *
 * Pulled out of the adapter so it can be tested and reused (and so the
 * adapter doesn't grow yet another lump of formatting logic in line with the
 * SSE branches). Unknown reasons fall through to a generic message — this is
 * fail-soft on purpose, since server and client may temporarily be on
 * mismatched versions across a deploy.
 */
export function buildUploadErrorMessage(event: Record<string, unknown>): string {
  const reason = event.reason as UploadErrorReason | undefined;
  const filename = (event.filename as string | undefined) ?? 'file';
  if (reason === undefined) {
    return `"${filename}" could not be attached.`;
  }
  switch (reason) {
    case 'too-large': {
      const maxBytes = event.maxBytes as number | undefined;
      const mb = maxBytes ? (maxBytes / (1024 * 1024)).toFixed(0) : '32';
      return `"${filename}" is too large. Maximum attachment size is ${mb} MB.`;
    }
    case 'too-many': {
      const maxN = event.maxAttachmentsPerTurn as number | undefined;
      const dropped = event.droppedCount as number | undefined;
      return `Too many attachments. Only the first ${maxN ?? ''} files were accepted${
        dropped ? `; ${dropped} were dropped` : ''
      }.`;
    }
    case 'invalid-mime': {
      // PLT-6061: name the formats that DO work. "Not a supported file type"
      // on its own says what happened and not what to do next, and this is the
      // one rejection where the next step is a different file — so the list has
      // to be in the message. `allowedMimeTypes` is sent by the server on this
      // event (`chat/index.ts`); prefer it over the client's own copy of the
      // allowlist, so the sentence stays true if the two are ever a deploy
      // apart.
      //
      // The raw MIME type is deliberately absent from this sentence. It used to
      // be shown in parentheses, and `event.mimeType` is still populated — but
      // a string like `application/x-iwork-numbers-sffnumbers` is exactly the
      // provider-shaped noise this change set exists to keep away from the
      // customer, and it tells them nothing actionable that the filename and
      // the supported-formats sentence don't already tell them. It stays
      // diagnosable for us via `logAttachmentUploadError`, which every path that
      // can refuse a file calls. Don't reintroduce it here.
      const allowed = event.allowedMimeTypes as string[] | undefined;
      const formats = describeSupportedAttachmentFormats(
        Array.isArray(allowed) && allowed.length > 0 ? allowed : undefined,
      );
      return `"${filename}" is not a supported file type.${formats ? ` ${formats}` : ''}`;
    }
    case 'malformed':
      return `"${filename}" could not be read and was skipped.`;
    case 'docx-conversion-failed':
      // A `.docx` is converted to plain text server-side before it reaches the
      // model (both US and EU paths); this fires when that conversion can't
      // parse the file (not a valid Word document, or it carried no text).
      return `"${filename}" couldn't be read as a Word document and was skipped. Try re-saving it as .docx, or upload a PDF or plain-text version instead.`;
    case 'xlsx-conversion-failed':
      return `"${filename}" couldn't be read as a spreadsheet and was skipped. Try re-saving it, or upload a CSV version instead.`;
    case 'upload-failed-using-inline-fallback':
      return `"${filename}" could not be uploaded to the document service and was sent inline. Response may be slower.`;
    case 'provider-unsupported':
      return `"${filename}" couldn't be attached — file attachments aren't available in this chat right now. Paste the relevant text into the message instead.`;
    case 'attachments-too-large-combined': {
      const maxBytes = event.maxBytes as number | undefined;
      const mb = maxBytes ? (maxBytes / (1024 * 1024)).toFixed(0) : undefined;
      // PLT-8030: a workbook staged as a supplier import, whose other sheets were sent as a document
      // of their own. Only that document was dropped; the supplier sheet was imported.
      const subject =
        event.otherSheetsOnly === true
          ? `Only the supplier sheet of "${filename}" was sent: its other sheets were skipped`
          : `"${filename}" was skipped`;
      return `${subject} because the attachments are too large together${
        mb ? ` (combined limit is about ${mb} MB)` : ''
      }. Try sending fewer or smaller files.`;
    }
    case 'too-many-pages': {
      const maxPages = event.maxPages as number | undefined;
      const pageCount = event.pageCount as number | undefined;
      return `"${filename}"${pageCount ? ` has ${pageCount} pages and` : ''} exceeds the ${
        maxPages ?? 100
      }-page limit for PDFs. Try splitting it into smaller files.`;
    }
    case 'image-dimensions-too-large': {
      const maxPixels = event.maxDimensionPixels as number | undefined;
      const width = event.width as number | undefined;
      const height = event.height as number | undefined;
      // Advice stays format-neutral: what trips the pixel ceiling is as often a
      // scanned plan, a panorama or a poster-size chart export as a screenshot.
      return `"${filename}" ${width && height ? `is ${width}x${height} pixels, over` : 'is over'} the ${
        maxPixels ?? 8000
      }-pixel limit for image width and height, and was skipped. Try scaling the image down or cropping it.`;
    }
    default:
      // Server and client can be on mismatched versions across a deploy, so a
      // reason this build doesn't recognise falls through to a generic message
      // rather than an empty/undefined one.
      return `"${filename}" could not be attached.`;
  }
}
